import React from 'react';
import { AlertTriangleIcon, InfoIcon } from 'lucide-react';
import { SmartLink } from '../SmartLink';
import type { LegalBlock } from '../../types/legal';
export function LegalBlockView({
  block
}: {
  block: LegalBlock;
}) {
  switch (block.type) {
    case 'paragraph':
      return <p className="text-[16px] leading-[1.75] text-ink-soft" data-id="element-655" data-mp="2qzq5d1ga7hk2">{block.text}</p>;
    case 'list':
      {
        const ListTag = block.ordered ? 'ol' : 'ul';
        return <ListTag className={`space-y-2 pl-5 text-[16px] leading-[1.7] text-ink-soft ${block.ordered ? 'list-decimal' : 'list-disc'} marker:text-ink-muted`} data-id="element-656" data-mp="2vyjiutvkj8k8">
          {block.items.map(item => <li key={item} className="pl-1" data-id="element-657" data-mp="350andvedh9ci">
              {item}
            </li>)}
        </ListTag>;
      }
    case 'definitions':
      return <dl className="divide-y divide-canvas-line rounded-[12px] border border-canvas-line" data-id="element-658" data-mp="1am9mz636ceab">
          {block.items.map(item => <div key={item.term} className="grid grid-cols-1 gap-1 px-5 py-4 sm:grid-cols-[200px_minmax(0,1fr)] sm:gap-6" data-id="element-659" data-mp="1fktcfggz887f">
              <dt className="text-[15px] font-semibold text-ink" data-id="element-660" data-mp="ur8kjnv2gphq">{item.term}</dt>
              <dd className="text-[15px] leading-[1.65] text-ink-soft" data-id="element-661" data-mp="up45qavmq62o">{item.description}</dd>
            </div>)}
        </dl>;
    case 'table':
      return <div className="overflow-x-auto rounded-[12px] border border-canvas-line" tabIndex={0} role="region" aria-label={block.caption} data-id="element-662" data-mp="ls3msjrbx06m">
          <table className="w-full min-w-[560px] border-collapse text-left" data-id="element-663" data-mp="1iegmpwx77ifj">
            <caption className="sr-only" data-id="element-664" data-mp="1efzjvpkt8u3x">{block.caption}</caption>
            <thead data-id="element-665" data-mp="19f21bbvw1y1w">
              <tr className="bg-canvas-warm" data-id="element-666" data-mp="19ejvbnjfc5gs">
                {block.columns.map(col => <th key={col} scope="col" className="whitespace-nowrap border-b border-canvas-line px-4 py-3 text-[13.5px] font-semibold text-ink" data-id="element-667" data-mp="14gic92m0065b">
                    {col}
                  </th>)}
              </tr>
            </thead>
            <tbody data-id="element-668" data-mp="3qooaavjtcy8b">
              {block.rows.map((row, r) => <tr key={r} className="even:bg-canvas-warm/50" data-id="element-669" data-mp="3qmbpmamo89rk" data-mpi={r}>
                  {row.map((cell, c) => c === 0 ? <th key={c} scope="row" className="border-b border-canvas-line px-4 py-3 text-[14.5px] font-medium text-ink last:border-b-0" data-id="element-670" data-mp="3ho9n1qnah926">
                        {cell}
                      </th> : <td key={c} className="border-b border-canvas-line px-4 py-3 text-[14.5px] tabular-nums text-ink-soft" data-id="element-671" data-mp="37wdlg5lzv0t7">
                        {cell}
                      </td>)}
                </tr>)}
            </tbody>
          </table>
        </div>;
    case 'callout':
      {
        const warning = block.tone === 'warning';
        const Icon = warning ? AlertTriangleIcon : InfoIcon;
        return <div className={`rounded-[12px] border px-5 py-5 ${warning ? 'border-[#EBCB9C] bg-[#FBF1E3]' : 'border-brand/20 bg-brand-soft'}`} data-id="element-672" data-mp="2typ0kxrxfto5">
          <div className="flex gap-3" data-id="element-673" data-mp="2acvt28mhlot0">
            <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${warning ? 'text-[#9A5B15]' : 'text-brand'}`} aria-hidden="true" data-id="element-674" data-mp="25dajz5dls02k" />
            <div data-id="element-675" data-mp="2kb2ybu7eyif6">
              <p className="text-[15.5px] font-semibold text-ink" data-id="element-676" data-mp="2kbkyv313zisw">{block.title}</p>
              <p className="mt-1.5 text-[15px] leading-[1.65] text-ink-soft" data-id="element-677" data-mp="2fb73olklw3ca">{block.text}</p>
              {block.action && <SmartLink href={block.action.href} className="mt-4 inline-flex h-11 items-center rounded-[7px] bg-brand px-5 text-[15px] font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-hover" data-id="element-678" data-mp="2t9n9maan9ptd">
                  {block.action.label}
                </SmartLink>}
            </div>
          </div>
        </div>;
      }
    case 'steps':
      return <ol className="grid grid-cols-1 gap-0 md:grid-cols-4 md:gap-4" data-id="element-679" data-mp="380e2llnz2uy2">
          {block.items.map((step, i) => <li key={step.title} className="relative flex gap-4 pb-7 last:pb-0 md:flex-col md:gap-0 md:pb-0" data-id="element-680" data-mp="15e8piwllkfqy" data-mpi={i}>
              {i < block.items.length - 1 && <span aria-hidden="true" className="absolute bottom-0 left-[17px] top-10 w-px bg-canvas-line md:left-10 md:right-0 md:top-[18px] md:h-px md:w-auto" data-id="element-681" data-mp="1aa78u5s2j7ki" />}
              <span className="relative z-[1] flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-ink text-[14px] font-semibold text-white" data-id="element-682" data-mp="1k1jx6nenrfls">
                {i + 1}
              </span>
              <div className="md:mt-4" data-id="element-683" data-mp="rdxwpo0q8r35">
                <p className="text-[15.5px] font-semibold text-ink" data-id="element-684" data-mp="wd0tymagtt0k">{step.title}</p>
                <p className="mt-1.5 text-[14.5px] leading-[1.6] text-ink-soft" data-id="element-685" data-mp="10edzswdk5w7h">{step.text}</p>
                <p className="mt-2.5 inline-flex rounded-full bg-canvas-warm px-2.5 py-1 text-[12.5px] font-medium text-ink-muted" data-id="element-686" data-mp="27rxuasc3o4n8">
                  {step.timeframe}
                </p>
              </div>
            </li>)}
        </ol>;
    case 'contacts':
      return <ul className="grid grid-cols-1 overflow-hidden rounded-[12px] border border-canvas-line sm:grid-cols-2" data-id="element-687" data-mp="80fkhja2oyq">
          {block.items.map(item => {
          const Icon = item.icon;
          return <li key={item.label} className="flex gap-3.5 border-b border-canvas-line px-5 py-4 last:border-b-0 sm:odd:border-r sm:[&:nth-last-child(-n+2)]:border-b-0" data-id="element-688" data-mp="3ik6nfnlom27d">
                <Icon className="mt-0.5 h-5 w-5 shrink-0 text-brand" strokeWidth={1.9} aria-hidden="true" data-id="element-689" data-mp="3dmdggsck1461" />
                <div className="min-w-0" data-id="element-690" data-mp="38o3k4ef3fxiu">
                  <p className="text-[13px] font-medium text-ink-muted" data-id="element-691" data-mp="34jm50sjoi8ee">{item.label}</p>
                  <p className="mt-0.5 break-words text-[15px] font-semibold text-ink" data-id="element-692" data-mp="2zludw0quyqb9">{item.value}</p>
                </div>
              </li>;
        })}
        </ul>;
    case 'action':
      return <SmartLink href={block.href} className="inline-flex h-12 items-center rounded-[7px] bg-brand px-6 text-[15px] font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-hover" data-id="element-693" data-mp="xuzez01cub0b">
          {block.label}
        </SmartLink>;
  }
}